/**
 * ARM Homeowner — "My Belongings"
 * Public, read-mostly view of every item that ContentGuard has logged for
 * the homeowner's job. Supports filtering by area, approving disposal
 * requests, and seeing replacement-claim values.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, Home, Loader2, Boxes, Star, AlertTriangle,
  CheckCircle2, XCircle, Trash2, PackageCheck, Filter,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const hwToken = () => new URLSearchParams(window.location.search).get("t") || "";
const withToken = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(hwToken())}`;

const DISPOSITION_LABEL = {
  restore_onsite: "Will be restored on-site",
  restore_offsite: "Sent off-site for restoration",
  non_restorable_claim: "Non-restorable — filed as claim",
  homeowner_keeps: "You elected to keep",
  trash: "Disposal proposed — awaiting your approval",
  pending_adjuster: "Awaiting adjuster review",
};

const RESTORATION_LABEL = {
  not_started: "Not yet started",
  in_cleaning: "Cleaning in progress",
  in_restoration: "Restoration in progress",
  restoration_complete: "Restoration complete",
  returned: "Returned to you",
  disposed: "Disposed",
};

export default function MyBelongings() {
  const { requestId, token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterArea, setFilterArea] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [signing, setSigning] = useState(null);  // item_id being decided
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    try {
      const url = token
        ? `${API_BASE}/api/contents/belongings/by-token/${token}`
        : withToken(`${API_BASE}/api/contents/public/${requestId}/belongings`);
      const res = await fetch(url);
      const d = await res.json().catch(() => null);
      if (!res.ok) { setError(d?.detail || "Unable to load"); return; }
      setData(d);
    } catch (e) {
      setError("Unable to connect.");
    } finally { setLoading(false); }
  }, [requestId, token]);

  useEffect(() => { load(); /* poll every 30s for live updates */
    const i = setInterval(load, 30000); return () => clearInterval(i);
  }, [load]);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    return data.items.filter(i => {
      if (filterArea !== "all" && i.area_db_id !== filterArea) return false;
      if (filterStatus === "high_value" && !i.high_value_flag) return false;
      if (filterStatus === "pending_disposal" && (i.disposition !== "trash" || i.disposal_homeowner_approved)) return false;
      if (filterStatus === "non_restorable" && i.disposition !== "non_restorable_claim") return false;
      if (filterStatus === "restoration" && !["in_cleaning", "in_restoration"].includes(i.restoration_status)) return false;
      return true;
    });
  }, [data, filterArea, filterStatus]);

  const decide = async (itemId, approve) => {
    if (!name.trim()) { toast.error("Type your name first"); return; }
    setSigning(itemId);
    try {
      const res = await fetch(withToken(`${API_BASE}/api/contents/public/items/${itemId}/disposal-decision`), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approve, homeowner_name: name.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null);
        throw new Error(d?.detail || "Failed");
      }
      toast.success(approve ? "Approved — item will be disposed" : "Rejected — item will be kept");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally { setSigning(null); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
      <Card className="max-w-sm border-red-200">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
          <p className="text-sm font-semibold text-zinc-900 mb-1">Unable to load</p>
          <p className="text-xs text-zinc-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const { request, session, areas, totals, pending_disposal_approvals } = data;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="my-belongings-page">
      <header className="bg-gradient-to-b from-zinc-900 to-zinc-800 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 text-xs text-zinc-300 mb-1">
            <Home className="w-3.5 h-3.5" /> {request.property_address_line1}, {request.property_city}
          </div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-red-400" /> My Belongings
          </h1>
          <p className="text-xs text-zinc-400 mt-1">
            {request.request_code} · {request.service_type_label}
          </p>
          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            {[
              ["Items", totals.items], ["Areas", totals.areas],
              ["Boxes", totals.boxes], ["Claims", totals.non_restorable],
            ].map(([l, v]) => (
              <div key={l} className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                <p className="text-[10px] text-zinc-300 uppercase tracking-wider font-bold">{l}</p>
                <p className="text-xl font-bold">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">

        {pending_disposal_approvals.length > 0 && (
          <Card className="border-amber-300 bg-amber-50/40" data-testid="pending-approvals-card">
            <CardContent className="p-4">
              <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Disposal Approval Needed ({pending_disposal_approvals.length})
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Type your name below, then approve or reject each item's disposal.
              </p>
              <Input value={name} onChange={e => setName(e.target.value)}
                     placeholder="Your full name"
                     className="mt-2 bg-white" data-testid="homeowner-name-input" />
              <div className="space-y-2 mt-3">
                {pending_disposal_approvals.map(it => (
                  <div key={it.item_id} className="bg-white rounded-lg border border-amber-200 p-3"
                       data-testid={`pending-${it.item_id}`}>
                    <div className="flex gap-3">
                      {it.photos[0] && (
                        <img src={it.photos[0].image_base64} alt=""
                             className="w-16 h-16 object-cover rounded border border-amber-200" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 truncate">
                          {it.ai_description?.full_description || it.category}
                        </p>
                        <p className="text-[10px] text-zinc-500">Condition: {it.condition || "—"} · Severity: {it.damage_severity || "—"}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button onClick={() => decide(it.item_id, true)}
                              disabled={signing === it.item_id || !name.trim()}
                              size="sm"
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs"
                              data-testid={`approve-${it.item_id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Approve Disposal
                      </Button>
                      <Button onClick={() => decide(it.item_id, false)}
                              disabled={signing === it.item_id || !name.trim()}
                              variant="outline" size="sm"
                              className="flex-1 text-xs"
                              data-testid={`reject-${it.item_id}`}>
                        <PackageCheck className="w-3.5 h-3.5 mr-1" /> Keep It
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border border-zinc-200">
          <CardContent className="p-3 flex items-center gap-2 overflow-x-auto">
            <Filter className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <select value={filterArea} onChange={e => setFilterArea(e.target.value)}
                    className="text-xs border border-zinc-200 rounded px-2 py-1"
                    data-testid="filter-area">
              <option value="all">All areas</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    className="text-xs border border-zinc-200 rounded px-2 py-1"
                    data-testid="filter-status">
              <option value="all">All items</option>
              <option value="high_value">High-value only</option>
              <option value="pending_disposal">Pending disposal</option>
              <option value="non_restorable">Non-restorable claims</option>
              <option value="restoration">In restoration</option>
            </select>
            <span className="ml-auto text-xs text-zinc-500">{filteredItems.length}</span>
          </CardContent>
        </Card>

        <div className="space-y-2">
          {filteredItems.map(it => {
            const area = areas.find(a => a.id === it.area_db_id);
            return (
              <Card key={it.item_id} className="border border-zinc-200" data-testid={`belonging-${it.item_id}`}>
                <CardContent className="p-3">
                  <div className="flex gap-3">
                    {it.photos[0] && (
                      <img src={it.photos[0].image_base64} alt=""
                           className="w-20 h-20 object-cover rounded border border-zinc-200 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-zinc-900 truncate">
                          {it.ai_description?.full_description || it.category}
                        </p>
                        {it.high_value_flag && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 flex-shrink-0" />}
                      </div>
                      <p className="text-[10px] text-zinc-500">{area?.name || "—"} · {it.category}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {it.disposition && (
                          <Badge className={`text-[10px] border ${
                            it.disposition === "trash" ? "bg-red-100 text-red-700 border-red-200"
                              : it.disposition === "non_restorable_claim" ? "bg-amber-100 text-amber-700 border-amber-200"
                              : "bg-blue-100 text-blue-700 border-blue-200"
                          }`}>
                            {DISPOSITION_LABEL[it.disposition] || it.disposition}
                          </Badge>
                        )}
                        {it.restoration_status && it.restoration_status !== "not_started" && (
                          <Badge className="text-[10px] bg-zinc-100 text-zinc-700 border-zinc-200 border">
                            {RESTORATION_LABEL[it.restoration_status]}
                          </Badge>
                        )}
                      </div>
                      {it.disposition === "non_restorable_claim" && it.acv_estimated_value && (
                        <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-1.5">
                          Claim value: <strong>ACV ${it.acv_estimated_value}</strong> / RCV ${it.rcv_estimated_value}
                          {it.replacement_recommendation_url && (
                            <a href={it.replacement_recommendation_url} target="_blank" rel="noreferrer"
                               className="block underline text-emerald-800 mt-0.5">
                              See replacement match →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {filteredItems.length === 0 && (
            <p className="text-xs text-zinc-400 italic text-center py-8">No items match this filter.</p>
          )}
        </div>

        {session.homeowner_signoff && (
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-3 flex items-center gap-2 text-xs text-emerald-800">
              <CheckCircle2 className="w-4 h-4" /> Inventory signed off on{" "}
              {new Date(session.homeowner_signoff.signed_at).toLocaleString()}.
            </CardContent>
          </Card>
        )}

        <p className="text-[10px] text-zinc-400 text-center pb-6">
          Powered by ContentGuard™ · Apex FieldProIQ
        </p>
      </main>
    </div>
  );
}
